import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggleState, useTranslateOptions } from '@anytag/shared-hooks';
import { getNumberValidFilter } from '@anytag/shared-utils';
import { Form, UploadButton } from '@anytag/shared-atoms';
import { MarketplaceCampaignAppliedStatus, MarketplaceDetail } from '@anytag/graphql';
import { SelectorField, TextSearchField, UploadCsvModal } from '@anytag/shared-molecules';
import { AdvancedFilter } from './AdvancedFilter';
import { MarketplaceApplicantsFilterFormKeys, MarketplaceApplicantsFilterFormValues } from './schemaTypes';
import { useUploadMarketplaceApplicantsFile } from './hooks';

export interface BasicFilterProps {
  onSubmit?: () => void;
  marketplace: MarketplaceDetail;
}

export const BasicFilter = ({ onSubmit, marketplace }: BasicFilterProps) => {
  const { t } = useTranslation();
  const uploadModal = useToggleState();
  const { getValues } = useFormContext<MarketplaceApplicantsFilterFormValues>();
  const { uploading, handleUploadFile } = useUploadMarketplaceApplicantsFile({ campaignId: marketplace.id });
  const allAppliedStatusesOptions = useTranslateOptions([
    { value: MarketplaceCampaignAppliedStatus.APPROVED, label: 'Approved' },
    { value: MarketplaceCampaignAppliedStatus.REJECTED, label: 'Rejected' },
    { value: MarketplaceCampaignAppliedStatus.APPLYING, label: 'Applied' },
    { value: MarketplaceCampaignAppliedStatus.REVIEWING, label: 'Reviewing' }
  ]);

  const values = getValues();
  const basicFilterKeys: MarketplaceApplicantsFilterFormKeys[] = ['keyword', 'appliedStatus'];
  const numOfValidFilter = getNumberValidFilter(values, basicFilterKeys);

  return (
    <>
      <Form.Fields>
        <TextSearchField<MarketplaceApplicantsFilterFormKeys>
          name="keyword"
          onSubmit={onSubmit}
          placeholder={t<string>('Search.InfluencerFilter')}
        />
        <SelectorField<MarketplaceApplicantsFilterFormKeys>
          onSubmit={onSubmit}
          name="appliedStatus"
          className="max-w-50"
          options={allAppliedStatusesOptions}
          placeholder={t<string>('Selector.Status')}
        />
        <AdvancedFilter count={numOfValidFilter} onSubmit={onSubmit} marketplace={marketplace} />
        <Form.Field className="flex-none">
          <UploadButton onClick={uploadModal.open} loading={uploading} />
        </Form.Field>
      </Form.Fields>

      <UploadCsvModal
        uploading={uploading}
        open={uploadModal.status}
        onClose={uploadModal.close}
        onCustomUpload={handleUploadFile}
        dropAreaDescription={t('drop the file and upload')}
        description={t('mkpApplicantsBulkUploadCsvDescription')}
        notes={[t('General.DragAndDropMaxSize', { maxSize: '10MB' }), t('General.DragAndDropMIME', { MIME: 'CSV' })]}
      />
    </>
  );
};
